import DownloadOutlined from "@mui/icons-material/DownloadOutlined";
import TuneRounded from "@mui/icons-material/TuneRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import size from "lodash/size";
import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import LocalStorageOptionRenderer from "../../_src_shared/components/LocalStorageOptionRenderer";
import LocalStorageQuickSaveButton from "../../_src_shared/components/LocalStorageQuickSaveButton";
import RffSelectRadioField from "../../components/finalForm/inputs/RffSelectRadioField";
import RffTextField from "../../components/finalForm/inputs/RffTextField";
import RffForm from "../../components/finalForm/RffForm";
import RffGroup from "../../components/finalForm/shared/RffGroup";
import FileUploadField from "../../components/inputs/FileUploadField";
import Flx from "../../components/layout/Flx";
import ScreenContent from "../../components/layout/ScreenContent";
import Htag from "../../components/typography/Htag";
import Txt from "../../components/typography/Txt";
import TitledCard from "../../components/ui/TitledCard/TitledCard";
import { selectOptionsPipelineType } from "../../constants/selectOptions/selectOptionsPipelineType";
import { useEnv } from "../../hooks/useEnv";
import { useLoanFilesHook } from "../../hooks/useLoanFilesHook";
import { useUnderwritingHook } from "../../hooks/useUnderwritingHook";
import { loanPipelineClear } from "../../redux/actions/loanPipelineActions";
import BorrowersSection from "./BorrowersSection";
import BorrowingEntitiesSection from "./BorrowingEntitiesSection";
import FileUploadSection from "./FileUploadSection";
import LoanDetailSection from "./LoanDetailSection";
import PropertiesSection from "./PropertiesSection";

const lsPrefix = "loanQuickIntakeFormScreenSaves";

const LoanQuickIntakeFormScreen = () => {
  const { isResfin } = useEnv();
  const defaultValues = useMemo(() => {
    return {
      borrowers: [{}],
      subjectProperties: [{}],
      pipeline: "Underwriting",
    };
  }, []);

  const [success, setSuccess] = useState(false);
  const dispatch = useDispatch();

  const quickSave = localStorage.getItem(`${lsPrefix}-quickSave`);
  const storedLayouts = localStorage.getItem(lsPrefix);

  const [quickSaveData, setQuickSaveData] = useState(
    quickSave ? JSON.parse(quickSave) : null
  );

  const [templates, setTemplates] = useState(
    storedLayouts ? JSON.parse(storedLayouts) : []
  );

  const [formData, setFormData] = useState(quickSaveData);
  const { loading, createLoan, createLoanWithFileUploads } =
    useUnderwritingHook();

  const navigate = useNavigate();

  const handleCreateLoanSuccess = () => {
    resetFormData();
    localStorage.removeItem(`${lsPrefix}-quickSave`);
    setSuccess(true);
    dispatch(loanPipelineClear());
    navigate("/loans");
  };

  const onFormSubmit = (formData) => {
    let data = { ...formData };
    let loanName = formData?.loanName;
    if (isNil(loanName) && isArray(data?.subjectProperties)) {
      const propertyName = data?.subjectProperties[0]?.address?.fullAddress;
      if (propertyName) {
        loanName = propertyName;
      }
    }

    data.loanName = loanName;

    if (isArray(data?.uploadMetadata) && size(data?.uploadMetadata) >= 1) {
      createLoanWithFileUploads({
        data: data,
        onSuccessFn: (d) => {
          localStorage.removeItem(`${lsPrefix}-quickSave`);
          handleCreateLoanSuccess();
        },
      });
    } else {
      createLoan({
        data: data,
        onSuccessFn: (d) => {
          handleCreateLoanSuccess();
        },
      });
    }
  };

  const resetFormData = useCallback(() => {
    setFormData(defaultValues);
    setQuickSaveData(null);
  }, [defaultValues]);

  const initialState = useMemo(() => {
    if (isNil(formData)) {
      return defaultValues;
    }
    return formData;
  }, [formData, defaultValues]);

  return (
    <>
      <RffForm
        className={"required"}
        loading={loading}
        success={success}
        submitText="Create Loan"
        maxWidth={"md"}
        sx={{
          pb: 6,
          background: "#f5f8fa",
          ".titled-group-body-root": {
            p: 2.5,
          },
          ".titled-group-header-root": {
            // mb: 1,
            "h1, svg": {
              color: "var(--titleColor)",
            },
          },
          h4: {
            fontWeight: 500,
            fontSize: "17px !important",
          },
        }}
        startContent={
          <Box
            sx={{
              background: "#ffffff",
              pt: 3,
              pb: 3,
              boxShadow: "0 0 0px 1px #dde0e4",
            }}
          >
            <ScreenContent suppressPaddingTopBottom>
              <Flx column gap={3}>
                <Flx fw jb ac>
                  <Flx column gap={1}>
                    <Htag h1>Create New Loan</Htag>
                    <Txt body2>
                      Upload the quick intake excel template, or fill out the
                      following questions.
                    </Txt>
                  </Flx>

                  <LocalStorageQuickSaveButton
                    lsPrefix={lsPrefix}
                    resetFormData={resetFormData}
                    onSaveUpdated={setTemplates}
                    quickSaveData={quickSaveData}
                    onUpdated={setQuickSaveData}
                    initialValues={initialState}
                  />
                </Flx>
                <TemplatesRenderer
                  lsPrefix={lsPrefix}
                  saves={templates}
                  onUpdate={setTemplates}
                />
              </Flx>
            </ScreenContent>
          </Box>
        }
        initialValues={initialState}
        onSubmit={onFormSubmit}
        formTitle={"Create Loan"}
        formDescription="Please provide as much information as possible regarding your loan, borrowers, and properties."
        finalStepLabel="Submit"
      >
        <Flx column gap={4} sx={{ p: 2 }}>
          {isResfin ? null : <UploadExcelContent setFormData={setFormData} />}
          <LoanDetailSection />
          <BorrowersSection />
          <PropertiesSection />
          <BorrowingEntitiesSection />
          <FileUploadSection />
          <TitledCard
            icon={<TuneRounded className="thin" />}
            variant="h1"
            title="Loan Configuration"
          >
            <RffGroup suppressBottomMargin>
              <RffTextField
                name="loanName"
                label="What do you want this loan to be called?"
              />

              <RffSelectRadioField
                name="pipeline"
                label="Select a pipeline to place this loan in"
                options={selectOptionsPipelineType}
              />
            </RffGroup>
          </TitledCard>
        </Flx>
      </RffForm>
    </>
  );
};

const TemplatesRenderer = ({ saves, onUpdate }) => {
  if (isEmpty(saves)) {
    return null;
  }

  return (
    <Flx ac gap={1}>
      <Txt sx={{ flexShrink: 0 }}>Saved Templates:</Txt>
      <LocalStorageOptionRenderer
        saves={saves}
        lsPrefix={lsPrefix}
        onUpdate={onUpdate}
      />
    </Flx>
  );
};

const DownloadLoanUploadTemplate = ({ children }) => {
  const { loading, downloadLoanUploadTemplate } = useLoanFilesHook();

  const onClick = () => {
    downloadLoanUploadTemplate({});
  };
  return (
    <Button
      loading={loading}
      fullWidth={false}
      variant="text"
      onClick={onClick}
      startIcon={<DownloadOutlined />}
    >
      Download Excel Template
    </Button>
  );
};

const UploadExcelContent = ({ setFormData }) => {
  const { loading, populateLoanFormFromExcel } = useUnderwritingHook();

  const onUpload = (excel) => {
    populateLoanFormFromExcel({
      excel: excel[0],
      onSuccessFn: (data) => {
        setFormData(data);
      },
    });
  };
  return (
    <>
      <Box
        sx={{
          pt: 2,
        }}
      >
        <Flx fw jb ac>
          <Txt>Quick Intake Excel Template Upload</Txt>
          <DownloadLoanUploadTemplate />
        </Flx>
        <FileUploadField
          suppressGrid
          sx={{
            width: "100%",
          }}
          onFilesAdded={onUpload}
          suppressFileList
          loading={loading}
          borderSize={3}
          minHeight="100px"
          suppressUploadButton
          uploadText={"Upload Quick Intake Loan Template Excel"}
        />
      </Box>
    </>
  );
};

export default LoanQuickIntakeFormScreen;
